import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

/**
 * "What changed today" — a 4-up briefing strip placed directly under
 * the hero, in lieu of a logo marquee. Reuters-leader-board feel.
 * Each card shows: a one-letter kind chip, the headline, mini sparkline
 * detail, and a JST timestamp. Hover: subtle elevation only.
 */
export function BriefStrip() {
  const { t } = useTranslation();

  const cards = [
    {
      kind: 'FUND',
      tint: '#10B981',
      headlineKey: 'brief.card_funding_headline',
      bodyKey:     'brief.card_funding_body',
      figure: '¥1.2B',
      time: '03:14',
    },
    {
      kind: 'JOB',
      tint: '#F59E0B',
      headlineKey: 'brief.card_jobchange_headline',
      bodyKey:     'brief.card_jobchange_body',
      figure: 'SDR → VP',
      time: '03:09',
    },
    {
      kind: 'TDN',
      tint: '#E11D48',
      headlineKey: 'brief.card_tdnet_headline',
      bodyKey:     'brief.card_tdnet_body',
      figure: '+12%',
      time: '02:54',
    },
    {
      kind: 'HIR',
      tint: '#0A5BFF',
      headlineKey: 'brief.card_hiring_headline',
      bodyKey:     'brief.card_hiring_body',
      figure: '8 roles',
      time: '02:38',
    },
  ];

  return (
    <section className="relative">
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14 md:py-16">
        {/* Section label */}
        <div className="flex items-baseline justify-between gap-4 mb-8">
          <div>
            <p className="text-[10.5px] uppercase tracking-[0.22em] text-ink-500 font-semibold">
              {t('brief.eyebrow')}
            </p>
            <h2 className="display serif-jp mt-2 text-[1.7rem] md:text-[2.05rem] font-medium tracking-tight text-ink-900 leading-tight">
              {t('brief.title')}
            </h2>
          </div>
          <div className="hidden sm:flex items-center gap-2 text-[10.5px] uppercase tracking-[0.16em] text-ink-500">
            <span className="num text-ink-900 font-semibold">{t('brief.stamp_date')}</span>
            <span className="h-3 w-px bg-ink-300" />
            <span className="num">{t('brief.stamp_origin')}</span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-ink-200 border border-ink-200 rounded-lg overflow-hidden">
          {cards.map((c, i) => (
            <motion.article
              key={c.kind + i}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-50px' }}
              transition={{ duration: 0.45, delay: i * 0.08 }}
              className="group relative bg-white p-5 hover:bg-ink-50 transition-colors"
            >
              <header className="flex items-center justify-between mb-4">
                <span
                  className="num text-[10px] font-semibold tracking-[0.18em] px-1.5 py-0.5 rounded"
                  style={{ color: c.tint, backgroundColor: `${c.tint}1a` }}
                >
                  {c.kind}
                </span>
                <span className="num text-[10.5px] text-ink-500">{c.time} JST</span>
              </header>

              <h3 className="text-[15px] font-semibold text-ink-900 leading-snug">
                {t(c.headlineKey)}
              </h3>
              <p className="mt-2 text-[12.5px] text-ink-600 leading-relaxed">
                {t(c.bodyKey)}
              </p>

              {/* Figure rail at bottom */}
              <div className="mt-5 pt-4 border-t border-ink-200 flex items-baseline justify-between">
                <span className="num text-xl font-medium tracking-tight tabular-nums text-ink-900">
                  {c.figure}
                </span>
                <span className="text-[10.5px] uppercase tracking-[0.16em] text-ink-500 group-hover:text-accent-700 transition-colors">
                  {t('brief.row_view')} →
                </span>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
